import { useEffect, useRef, useState } from "react";
import { useEditorCommands, useEditorState } from "@linempo/react-state";
import { subtitleCues } from "@linempo/project-domain";
import { errorMessage } from "../../editor-geometry.js";
import { Dialog } from "../project/Dialog.js";

type ImportMode = "replace" | "append" | "cancel";
export interface ImportRecord { readonly id: string; readonly name: string; readonly kind: "subtitle" | "image"; readonly status: "importing" | "ready" | "error" | "cancelled"; readonly detail: string; readonly assetId?: string }

/** Files stay in this feature's refs. Only small progress records reach React state. */
export function useAssetImport(onError: (message: string) => void) {
  const commands = useEditorCommands(); const state = useEditorState();
  const stateRef = useRef(state); stateRef.current = state;
  // A resolved import can precede React's next render. Keep that fact for the next
  // file in the serial queue, then refresh it whenever a new revision is rendered.
  const subtitleKnowledge = useRef({ projectId: state.project?.id, revision: state.project?.revision, present: !!state.project && subtitleCues(state.project).length > 0 });
  if (subtitleKnowledge.current.projectId !== state.project?.id || subtitleKnowledge.current.revision !== state.project?.revision) {
    subtitleKnowledge.current = { projectId: state.project?.id, revision: state.project?.revision, present: !!state.project && subtitleCues(state.project).length > 0 };
  }
  const files = useRef(new Map<string, File>());
  const queue = useRef(Promise.resolve()); const active = useRef(true);
  const [records, setRecords] = useState<readonly ImportRecord[]>([]);
  const [pending, setPending] = useState(0);
  const [question, setQuestion] = useState<{ name: string; resolve: (mode: ImportMode) => void } | null>(null);
  const questionRef = useRef(question); questionRef.current = question;
  useEffect(() => { active.current = true; return () => { active.current = false; questionRef.current?.resolve("cancel"); files.current.clear(); }; }, []);
  const projectId = state.project?.id;
  useEffect(() => { setRecords([]); files.current.clear(); }, [projectId]);
  const update = (id: string, change: Partial<ImportRecord>) => { if (active.current) setRecords((items) => items.map((item) => item.id === id ? { ...item, ...change } : item)); };
  const processFile = async (id: string, file: File, expectedProjectId: string) => {
    if (!active.current || stateRef.current.project?.id !== expectedProjectId) return;
    update(id, { status: "importing", detail: "处理中…" });
    try {
      if (/\.srt$/i.test(file.name)) {
        if (file.size > 8 * 1024 * 1024) throw new Error("字幕文件太大，请使用 8 MiB 以内的 SRT。");
        const source = await file.text();
        const mode = subtitleKnowledge.current.present ? await new Promise<ImportMode>((resolve) => {
          if (!active.current) { resolve("cancel"); return; }
          const next = { name: file.name, resolve }; questionRef.current = next; setQuestion(next);
        }) : "replace";
        if (mode === "cancel" || !active.current) { update(id, { status: "cancelled", detail: "已取消" }); files.current.delete(id); return; }
        const track = await commands.importSubtitle(source, file.name, mode);
        subtitleKnowledge.current.present = true;
        update(id, { status: "ready", detail: `已导入 ${track.cues.length} 条字幕` });
      } else {
        if (!/\.(png|jpe?g|webp)$/i.test(file.name) && !["image/png", "image/jpeg", "image/webp"].includes(file.type)) throw new Error("请选择 SRT、PNG、JPEG 或 WebP 文件。");
        const asset = await commands.importAsset(file);
        update(id, { status: "ready", assetId: asset.id, detail: "已导入" });
        await commands.selectAsset(asset.id);
      }
      files.current.delete(id);
    } catch (error) {
      const duplicate = typeof error === "object" && error !== null && "path" in error && error.path === "edit.duplicate-subtitles";
      const detail = duplicate ? "已导入：这份字幕已在工程中。" : errorMessage(error);
      update(id, { status: duplicate ? "ready" : "error", detail });
      if (duplicate) { files.current.delete(id); commands.clearError(); }
      else if (active.current) onError(detail);
    }
  };
  const enqueue = (id: string, file: File) => {
    const current = stateRef.current;
    if (!current.project || current.export.locked || current.workspace.switching) return;
    const idAtStart = current.project.id;
    setPending((count) => count + 1);
    queue.current = queue.current.then(() => processFile(id, file, idAtStart)).finally(() => { if (active.current) setPending((count) => count - 1); });
  };
  const importFiles = (incoming: FileList | readonly File[]) => {
    if (!stateRef.current.project || stateRef.current.export.locked) return;
    for (const file of Array.from(incoming)) {
      const id = crypto.randomUUID(); files.current.set(id, file);
      setRecords((items) => [...items, { id, name: file.name, kind: /\.srt$/i.test(file.name) ? "subtitle" : "image", status: "importing", detail: "等待处理…" }]);
      enqueue(id, file);
    }
  };
  const retry = (id: string) => { const file = files.current.get(id); if (file) enqueue(id, file); };
  const answer = (mode: ImportMode) => { questionRef.current?.resolve(mode); questionRef.current = null; setQuestion(null); };
  return { records, pending, importFiles, retry, dialog: question && <Dialog title="导入新的字幕" onClose={() => answer("cancel")}>
    <p>工程中已有字幕。如何导入「{question.name}」？</p><p className="help-text">替换会保留镜头时段并更新字幕关联；追加保留原始时码。两种操作均可撤销。</p>
    <div className="dialog-actions"><button type="button" className="button" onClick={() => answer("cancel")}>取消</button><button type="button" className="button" onClick={() => answer("append")}>追加字幕</button><button type="button" className="button dark" onClick={() => answer("replace")}>替换字幕</button></div>
  </Dialog> };
}
